import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useState } from 'react'
import {
	AuthHeading,
	AuthLayout,
	AuthLeftBlock,
	AuthRightBlock,
	Button,
	Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
	Input,
	Stepper,
} from '@rentflowdev/ui'
import { useGetCompanyProfileQuery, useUpdateUserMutation, useUpsertCompanyProfileMutation } from '@/api'
import { useFormDraft } from '@/lib/wizard-drafts-store'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import {
	ONBOARDING_DRAFT_KEY,
	type OnboardingDraft,
	onboardingTotalSteps,
} from './account-type'

export const Route = createFileRoute('/onboarding/profile')({
	component: ProfilePage,
})

/** Strict subset of Prisma `User`. `name`, the display name, is derived by the server. */
const personSchema = z.object({
	firstName: z.string().trim().min(1, 'onboarding.profile.firstNameRequired'),
	lastName:  z.string().trim().min(1, 'onboarding.profile.lastNameRequired'),
})

/** Strict subset of Prisma `CompanyProfile`. */
const companySchema = z.object({
	name: z.string().trim().min(1, 'onboarding.profile.companyNameRequired'),
})

type PersonFormValues = z.infer<typeof personSchema>
type CompanyFormValues = z.infer<typeof companySchema>

function ProfilePage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const draft = useFormDraft<OnboardingDraft>(ONBOARDING_DRAFT_KEY)
	const accountType = draft.read()?.state?.accountType ?? 'individual'
	const total = onboardingTotalSteps(accountType)

	const finish = () => {
		draft.reset()
		void navigate({ to: '/' })
	}
	const back = () =>
		void navigate({ to: accountType === 'individual' ? '/onboarding/account-type' : '/onboarding/tax-regime' })

	return (
		<AuthLayout>
			<AuthLeftBlock />

			<AuthRightBlock>
				<div className="flex flex-col w-full">
					<Stepper current={total} total={total} className="min-h-15" />
					<AuthHeading className="text-left">{t('onboarding.profile.title')}</AuthHeading>

					{accountType === 'company'
						? <CompanyForm onBack={back} onDone={finish} />
						: <PersonForm onBack={back} onDone={finish} />}
				</div>
			</AuthRightBlock>
		</AuthLayout>
	)
}

interface StepFormProps {
	onBack: () => void
	onDone: () => void
}

function PersonForm({ onBack, onDone }: StepFormProps) {
	const { t } = useTranslation()
	const [updateUser, { isLoading }] = useUpdateUserMutation()
	const [submitError, setSubmitError] = useState<string | null>(null)

	const form = useForm<PersonFormValues>({
		resolver: zodResolverI18n<PersonFormValues>(personSchema),
		defaultValues: { firstName: '', lastName: '' },
		mode: 'onChange',
	})

	const onSubmit = async (values: PersonFormValues) => {
		setSubmitError(null)
		try {
			await updateUser({ firstName: values.firstName, lastName: values.lastName }).unwrap()
			onDone()
		} catch {
			setSubmitError(t('onboarding.profile.saveError'))
		}
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
				<div className="flex flex-col gap-4 py-6">
					<FormField
						control={form.control}
						name="firstName"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('onboarding.profile.firstName')}</FormLabel>
								<FormControl>
									<Input placeholder={t('onboarding.profile.firstNamePlaceholder')} autoFocus autoComplete="given-name" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="lastName"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('onboarding.profile.lastName')}</FormLabel>
								<FormControl>
									<Input placeholder={t('onboarding.profile.lastNamePlaceholder')} autoComplete="family-name" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					{submitError && <p className="text-sm text-(--color-destructive)">{submitError}</p>}
				</div>

				<StepActions onBack={onBack} loading={isLoading} disabled={!form.formState.isValid} />
			</form>
		</Form>
	)
}

function CompanyForm({ onBack, onDone }: StepFormProps) {
	const { t } = useTranslation()
	const { data: existingProfile, isLoading: loadingProfile } = useGetCompanyProfileQuery()
	const [upsertCompany, { isLoading: savingCompany }] = useUpsertCompanyProfileMutation()
	const [updateUser, { isLoading: savingUser }] = useUpdateUserMutation()
	const [submitError, setSubmitError] = useState<string | null>(null)

	const form = useForm<CompanyFormValues>({
		resolver: zodResolverI18n<CompanyFormValues>(companySchema),
		defaultValues: { name: '' },
		mode: 'onChange',
	})

	const onSubmit = async (values: CompanyFormValues) => {
		setSubmitError(null)
		try {
			// PUT /account/company replaces the whole profile, so sending only the
			// name would wipe requisites already entered in settings. Create it here
			// only when the server confirmed there is none yet.
			if (existingProfile === null) await upsertCompany({ name: values.name }).unwrap()
			// The company name is also the account's display name: without one the
			// dashboard would keep sending the user back to onboarding.
			await updateUser({ name: values.name }).unwrap()
			onDone()
		} catch {
			setSubmitError(t('onboarding.profile.saveError'))
		}
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
				<div className="flex flex-col gap-4 py-6">
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('onboarding.profile.companyName')}</FormLabel>
								<FormControl>
									<Input placeholder={t('onboarding.profile.companyNamePlaceholder')} autoFocus autoComplete="organization" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					{submitError && <p className="text-sm text-(--color-destructive)">{submitError}</p>}
				</div>

				<StepActions onBack={onBack} loading={savingCompany || savingUser} disabled={!form.formState.isValid || loadingProfile} />
			</form>
		</Form>
	)
}

function StepActions({ onBack, loading, disabled }: { onBack: () => void; loading: boolean; disabled: boolean }) {
	const { t } = useTranslation()
	return (
		<div className="flex gap-2">
			<Button type="button" variant="outline" className="flex-1" onClick={onBack} disabled={loading}>
				{t('common.back')}
			</Button>
			<Button type="submit" className="flex-1" loading={loading} disabled={disabled}>
				{t('onboarding.profile.createAccount')}
			</Button>
		</div>
	)
}
